import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { Clapperboard, CheckCircle, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvitationAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [invite, setInvite] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | accepting | accepted | error
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/invitations/${token}`)
      .then(r => { setInvite(r.data); setStatus("ready"); })
      .catch(e => { setError(e.response?.data?.detail || "Invitation not found"); setStatus("error"); });
  }, [token]);

  const accept = async () => {
    setStatus("accepting");
    try {
      const { data } = await axios.post(`${API}/invitations/${token}/accept`, {}, { withCredentials: true });
      setStatus("accepted");
      setTimeout(() => navigate(`/projects/${data.project_id}`), 1200);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to accept invitation");
      setStatus("error");
    }
  };

  const emailMatches = user && invite && user.email?.toLowerCase() === invite.email?.toLowerCase();

  return (
    <div className="min-h-screen bg-[#0D0F12] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-[#14181F] border border-[#28303F] rounded-2xl p-8 shadow-2xl">
        <div className="flex items-center gap-2 mb-6">
          <Clapperboard className="text-amber-400" size={24} />
          <span className="text-xl font-bold text-white font-[Outfit]">Get2Set</span>
        </div>

        {status === "loading" && (
          <div className="text-center py-8">
            <Loader2 className="animate-spin text-amber-400 mx-auto mb-3" size={28}/>
            <p className="text-gray-400 text-sm">Loading invitation...</p>
          </div>
        )}

        {status === "error" && (
          <div className="text-center py-6">
            <XCircle className="text-red-400 mx-auto mb-3" size={36}/>
            <p className="text-white font-medium mb-2">Invitation unavailable</p>
            <p className="text-gray-500 text-sm mb-6" data-testid="invitation-error">{error}</p>
            <button onClick={() => navigate("/login")}
              className="bg-amber-500 hover:bg-amber-600 text-black font-bold px-5 py-2 rounded-lg text-sm">Go to Login</button>
          </div>
        )}

        {status === "accepted" && (
          <div className="text-center py-6" data-testid="invitation-accepted">
            <CheckCircle className="text-green-400 mx-auto mb-3" size={36}/>
            <p className="text-white font-medium mb-1">You're in!</p>
            <p className="text-gray-500 text-sm">Redirecting to the project...</p>
          </div>
        )}

        {(status === "ready" || status === "accepting") && invite && (
          <>
            <p className="text-xs text-amber-400/80 uppercase tracking-widest mb-2 font-mono">Project Invitation</p>
            <h1 className="text-2xl font-bold text-white font-[Outfit] mb-4">{invite.project_name}</h1>
            <p className="text-sm text-gray-400 mb-6">
              <span className="text-white">{invite.invited_by_name || invite.invited_by_email}</span> invited
              <span className="text-amber-400"> {invite.email}</span> to collaborate on this call-sheet project.
            </p>

            {loading ? (
              <p className="text-gray-500 text-sm">Checking your session…</p>
            ) : !user ? (
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-xs text-amber-300 mb-4">
                Please <button onClick={() => navigate("/login", { state: { from: `/invitations/${token}` } })} className="underline">sign in</button> as <b>{invite.email}</b> to accept.
              </div>
            ) : !emailMatches ? (
              <div className="bg-red-500/10 border border-red-500/40 rounded-lg p-3 text-xs text-red-300 mb-4">
                You're signed in as <b>{user.email}</b>, but this invitation is for <b>{invite.email}</b>. Sign out and sign in with the correct account.
              </div>
            ) : (
              <button data-testid="invitation-accept-btn"
                onClick={accept} disabled={status === "accepting"}
                className="w-full bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold py-3 rounded-lg text-sm">
                {status === "accepting" ? "Accepting..." : `Accept & Join "${invite.project_name}"`}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
